import { useEffect, useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { CustomWidthTooltip } from "mui-toolkit/CustomWidthTooltip";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { formatPrice } from "utility/formatters";

import { formatDate, formatPercentage } from "../../utility/formatters";
import { NavigateIcon } from "../Icons/Icons";

export default function EquipmentLoanDataTable({
  loans = [],
  onEdit,
  onDelete,
  isLoading,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "creditor",
        headerName: "Creditor",
        flex: 1.5,
      },
      {
        field: "balance",
        headerName: "Balance",
        flex: 1,
        valueGetter: (paramsOrValue, rowOrUndefined) => {
          try {
            const row = paramsOrValue?.row || rowOrUndefined || {};
            return (
              (Number(row?.liability_current) || 0) +
              (Number(row?.liability_intermediate) || 0) +
              (Number(row?.liability_longterm) || 0)
            );
          } catch (error) {
            console.warn("Error in balance valueGetter:", error);
            return 0;
          }
        },
        renderCell: (params) => {
          if (!params || !params.row) return "-";
          const loan = params.row;
          const balance =
            (Number(loan?.liability_current) || 0) +
            (Number(loan?.liability_intermediate) || 0) +
            (Number(loan?.liability_longterm) || 0);
          return formatPrice(balance, 0);
        },
      },
      {
        field: "interest_rate",
        headerName: "Rate",
        flex: 1,
        valueFormatter: (value) => (value ? formatPercentage(value, 0) : ""),
      },
      {
        field: "payment_frequency",
        headerName: "Schedule",
        flex: 1,
      },
      {
        field: "maturity_date",
        headerName: "Maturity Date",
        flex: 1.2,
        valueFormatter: (value) => (value ? formatDate(value) : ""),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 150,
        sortable: false,
        getActions: (params) => {
          return [
            <CustomWidthTooltip key="delete" title="Delete" placement="top" arrow>
              <DeleteConfirmationButton item={params.row} onChange={onDelete} />
            </CustomWidthTooltip>,
            <GridActionsCellItem
              key="edit"
              icon={<NavigateIcon />}
              label="Edit"
              data-testid={`edit-loan-${params.row.id}`}
              onClick={() => onEdit?.(params.row)}
            />,
          ];
        },
        flex: 0,
      },
    ],
    [onEdit, onDelete]
  );
  if (!isMounted) {
    return null;
  }
  return (
    <>
      <TilleyDataGrid
        rows={loans || []}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="equipment-loans"
        getRowId={(row) => row.id}
        getRowHeight={() => rowHeight}
        isLoading={isLoading}
        sx={{ width: "99%", boxSizing: "border-box" }}
      />
    </>
  );
}
